import { t } from "../../hooks/useAppTranslation";
import {useCallback, useEffect, useRef, useState} from "react";
import MaterialTable, {MTableAction, MTableActions, MTableBody, MTableHeader, MTableToolbar, MTableCell} from 'material-table';
import Paper from '@mui/material/Paper';
import './TableCustom.scss';
import debounce from "lodash.debounce";
const TableCustom = ({
                       data,
                       initialPage,
                       parentRef,
                       verticalAlign,
						count,
						page,
						pageSize,
						onFilterChange,
						orderBy,
						orderDirection,
						onOrderChange,
						 onQuickSearchChange,
						 quickSearch,
                       ...props
                     }) => {
  const toolbarRef = useRef();
  const containerRef = useRef();
  const isActionsMultilines = props.isActionsMultilines;
  const [pageLengthOptions, setPageLengthOptions] = useState([10, 20]);
  const [searchValue, setSearchValue] = useState('')
  const [orderByValue, setOrderByValue] = useState(-1)
  const [orderDirectionValue, setOrderDirectionValue] = useState('')

  const setAttr = (reference, querySelector, attr, attrVal, all = false) => {
    if (!reference.current) return;
    let selectField;

    if (all) {
      selectField = reference.current.querySelectorAll(querySelector);
      selectField.forEach(field => field.setAttribute(attr, attrVal));
    } else {
      selectField = reference.current.querySelector(querySelector);
      selectField && selectField.setAttribute(attr, attrVal);
    }
  };

  useEffect(() => {
    // Add aria-labels to columns names 
    const colNamesList = containerRef?.current.querySelectorAll('.MuiTableSortLabel-root');
    colNamesList.forEach(col => {
      col.setAttribute('aria-label', `${col.innerText.replace('arrow_downward', '')}. ${t("table-sort-column")}`)
    });

    // Add aria-label to filter fields
    const presentFieldsAriaLabeld = containerRef?.current.querySelectorAll('.MuiInputBase-input.MuiInput-input.MuiInputBase-inputAdornedStart.MuiInputBase-inputTypeSearch.MuiInput-inputTypeSearch');
    presentFieldsAriaLabeld.forEach(input => {
      const newName = input.getAttribute('aria-label').replace('filter data by', t("table-filter-column"));
      input.setAttribute('aria-label', `${newName}. ${t("table-search-hint")}`);

    });

    // Add aria-label to search field and search clear btn
    setAttr(toolbarRef, '.MuiInputBase-input', 'aria-label', t("table-search-hint"));
    setAttr(toolbarRef, '.MuiIconButton-root', 'aria-label', t("table-clear-search-field"));

    if (pageLengthOptions.length > 1) {
      // Add id and label to rows select
      setAttr(containerRef, '.MuiSelect-nativeInput', 'id', 'rowSelectId');
      setAttr(containerRef, '.MuiSelect-nativeInput', 'aria-describedby', 'rowSelectedLabelId');
      setAttr(containerRef, '.MuiInputBase-root.MuiTablePagination-input.MuiTablePagination-selectRoot', 'aria-label', t("table-expand-rows-options"));
    }
  }, [data, pageLengthOptions]);

  useEffect(() => {
    setSearchValue(quickSearch)
    setOrderDirectionValue(orderDirection)
    setOrderByValue((orderBy == null || orderBy === "") ? null : orderBy);

    const dataLength = count;

    if (dataLength > 20) {
      setPageLengthOptions([10, 20, 50]);
    }
    if (dataLength > 10 && dataLength <= 20) {
      setPageLengthOptions([10, 20]);
    }
    if (dataLength <= 10) {
      setPageLengthOptions([]);
    }
  }, [data]);

	const changeFilter = event => {
		onFilterChange(event)
	};

	const changeQuickSearch = event => {
		onQuickSearchChange(event)
	};

	const debouncedChangeHandler = useCallback(
		debounce(changeFilter, 700)
		, []);

	const debouncedQuickSearchChangeHandler = useCallback(
		debounce(changeQuickSearch, 700)
		, []);

  return (
    <div className='table-custom'>
      <MaterialTable
        title={props.customTabs}
        data={(query) =>
		{
			return new Promise((resolve, reject) => {
				resolve ( {
					data: data,
					totalCount: count,
					page: page,
				})
			})
		}

		}
        components={{
          Toolbar: props => (
            <div ref={toolbarRef}>
              {onQuickSearchChange && <MTableToolbar 
              {...props} 
              searchText={searchValue} 
              onSearchChanged={(el) => {
                props.onSearchChanged(el)
                debouncedQuickSearchChangeHandler(el)
			        }} />}
            </div>
          ),
          Container: props => (
            <div ref={containerRef}>
              <Paper variant='outlined' {...props} />
              <label htmlFor='rowSelectId' className='sr-only' id='rowSelectedLabelId'>{t("table-number-displayed-rows")}</label>
            </div>
          ),
          Action: props => (
            <div className='table-custom__action'>
              <MTableAction {...props} />
            </div>
          ),
          Actions: props => {
            return  isActionsMultilines ?
                    <div className='table-custom__actions'>
                        <MTableActions {...props} />
                    </div> :
                    <MTableActions {...props} />
          },
          Cell: props => <MTableCell {...props} className={props.columnDef.className ?? null} style={{ whiteSpace: 'pre'}} />,
			Header: props => <MTableHeader {...props}
				orderBy={orderByValue} orderDirection={orderDirectionValue}
			onOrderChange={(columnId, value) => {
				if (value === "") {
					columnId = -1
				}
				props.onOrderChange(columnId, value)
				onOrderChange(columnId, value)
				}
			}/>,
			Body: props => <MTableBody {...props}
			  onFilterChanged={(columnId, value) => {
          props.onFilterChanged(columnId, value);
          debouncedChangeHandler(props.columns.map(c => ({field: c.field, filterValue: c.tableData.filterValue})))
			}}/>
        }}
        options={{
          filtering: true,
          showTitle: props.isTableForDoneControls ? true : false,
          sorting: true,
          paginationType: 'stepped',
          pageSize: pageSize,
          pageSizeOptions: pageLengthOptions,
          headerStyle: {backgroundColor: '#0D47A1', color: 'white'},
          initialPage: 0,
          draggable: false,
          align: 'left',
          actionsColumnIndex: props.isTableForDoneControls ? 1 : null,
        }}
        localization={{
          pagination: {
            firstAriaLabel: t("table-first-page"),
            firstTooltip: t("table-first-page"),
            previousAriaLabel: t("table-previous-page"),
            previousTooltip: t("table-previous-page"),
            nextAriaLabel: t("table-next-page"),
            nextTooltip: t("table-next-page"),
            lastAriaLabel: t("table-last-page"),
            lastTooltip: t("table-last-page"),
            labelRowsPerPage: t("table-rows-per-page"),
            labelRowsSelect: t("table-rows")

          },
          toolbar: {
            searchTooltip: t("table-search"),
            searchPlaceholder: t("table-search"),

          },
          header: {
            actions: t("table-actions"),
          },
          body: {
            emptyDataSourceMessage: t("table-no-results"),
            filterRow: {
              filterTooltip: t("table-filter"),
            }
          }
        }}
        {...props}
      />
    </div>
  )
};

export default TableCustom;